import {Button, Banner} from '@sail/ui';
import {FormattedMessage, defineMessages} from 'react-intl';
import {useAnalytics} from '@stripe-internal/inline-data';
import {useEffect} from 'react';
import {widgetLinkAction} from '@stripe-internal/conversations-ui';
import {Audience, Payout} from './MerchantPayoutStatus';
import {
  buildFormattedSentTo,
  payoutDetailsNeedUpdate,
} from './StatusDescription';

const updatePayoutDestinationCTALink = (
  merchantToken?: string,
  audience?: Audience,
) =>
  audience === 'express'
    ? `https://connect.stripe.com/app/express#${merchantToken}/settings`
    : 'https://dashboard.stripe.com/settings/payouts?ref=universal-help-merchant-payout-flow';

const messages = defineMessages({
  updatePayoutDestinationCTA: {
    id: 'mse_inline_data.merchant_payout_status.update_payout_destination_cta',
    description:
      'Message displaying update Bank Account or other destination type.',
    defaultMessage: 'Update {sent_to} details',
  },
  updatePayoutDestinationNoPermissions: {
    id: 'mse_inline_data.merchant_payout_status.update_payout_destination_no_permissions',
    description:
      'Message displayed when logged-in user does not have permission to update payout details after a failure.',
    defaultMessage:
      'You do not have permission to update {sent_to} details for payouts on this account. Please reach out to your administrator.',
  },
});

export const UpdatePayoutDetailsPermissionNotice = ({
  payout,
  updatePayoutPermissions,
}: {
  payout: Payout;
  updatePayoutPermissions?: boolean;
}) => {
  const analytics = useAnalytics();
  const showBanner =
    payoutDetailsNeedUpdate(payout) && !updatePayoutPermissions;

  useEffect(() => {
    if (showBanner) {
      analytics.viewed(
        'uh_merchant_payout_status_failed_update_payout_details',
        {
          uh_merchant_payout_status_destination_type: payout?.destination?.type,
          uh_merchant_payout_status_failed_update_payout_permission:
            updatePayoutPermissions,
        },
      );
    }
  }, [
    analytics,
    payout?.destination?.type,
    showBanner,
    updatePayoutPermissions,
  ]);

  if (showBanner) {
    const formattedSentTo = buildFormattedSentTo(payout);
    return (
      <Banner
        type="default"
        description={
          <>
            <FormattedMessage
              {...messages.updatePayoutDestinationNoPermissions}
              values={{
                sent_to: formattedSentTo,
              }}
            />
          </>
        }
      />
    );
  }

  return <></>;
};

export const UpdatePayoutDetailsInlineButton = ({
  payout,
  updatePayoutPermissions,
  merchantToken,
  audience,
}: {
  payout: Payout;
  updatePayoutPermissions?: boolean;
  merchantToken?: string;
  audience?: Audience;
}) => {
  const analytics = useAnalytics();
  const showButton = payoutDetailsNeedUpdate(payout) && updatePayoutPermissions;

  useEffect(() => {
    if (showButton) {
      analytics.viewed(
        'uh_merchant_payout_status_failed_update_payout_details',
        {
          uh_merchant_payout_status_destination_type: payout?.destination?.type,
          uh_merchant_payout_status_failed_update_payout_permission:
            updatePayoutPermissions,
        },
      );
    }
  }, [
    analytics,
    payout?.destination?.type,
    showButton,
    updatePayoutPermissions,
  ]);

  if (showButton) {
    const formattedSentTo = buildFormattedSentTo(payout);
    return (
      <Button
        data-testid="cta-button"
        type="secondary"
        css={{width: 'fill', size: 'small'}}
        uses={[widgetLinkAction()]}
        href={updatePayoutDestinationCTALink(merchantToken, audience)}
        onPress={() => {
          analytics.action(
            'uh_merchant_payout_status_update_payout_destination_button_clicked',
          );
        }}
      >
        <FormattedMessage
          {...messages.updatePayoutDestinationCTA}
          values={{
            sent_to: formattedSentTo,
          }}
        />
      </Button>
    );
  }

  return <></>;
};
